import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  Button,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useSelector } from "react-redux";
import BasicProps from "./BasicProps";
import Extendedprops from "./ExtendedProps";
import { WidthProvider, Responsive } from "react-grid-layout";
// import CustomDialogBox from "../../../../CommonComponents/CustomDialogBox"; // Import CustomDialogBox
import "react-grid-layout/css/styles.css";
import Valuescard from "../../../../Widgets/Valuescard";

import {
  buildWidgetCardSx,
  widgetHeaderSx,
  widgetTitleSx,
  widgetContentSx,
  resolveWidgetBackground,
} from "./utils/widgetStyleTokens";

// Modular imports for widget types and chart option factory
import {
  MAIN_WIDGET_GROUPS as mainWidgetList,
  SUB_WIDGET_TYPES as subWidgetLists,
  isChartType,
  isGaugeType,
  isTableType,
} from "./utils/widgetTypes";
import {
  getChartOptions,
  listRegisteredChartTypes,
} from "./utils/chartOptionsFactory";
import CustomButton from "../../../../CommonComponents/CustomButton";
import EChart from "../../EChart";
import CustomDialogBox from "../../../../CommonComponents/CustomDialogBox";

// Local default extended props for newly created chart widgets (used when selecting a sub widget)
const chartDefaults = {
  smooth: false,
  symbol: "circle",
  lineStyle: { width: 2, color: "#3398DB" },
  areaStyle: { opacity: 0.2, color: "#CCE5FF" },
  label: { show: true, position: "top" },
  xAxisLabel: "",
  yAxisLabel: "",
  barWidth: "40%",
  itemStyle: { color: "#3398DB", borderRadius: [4, 4, 0, 0] },
  showBackground: true,
  backgroundStyle: { color: "rgba(200,200,200,0.2)", borderColor: "#ccc" },
  radius: "50%",
  roseType: false,
  valuePrecision: 2,
};

// Default chart options for preview (can be extended)
// Deprecated getDefaultChartOptions: replaced by registry-based getChartOptions
function getDefaultChartOptions(type, basicProps, extendedProps, datapoints) {
  return getChartOptions(type, basicProps, extendedProps, datapoints);
}

const WidgetForm = forwardRef(function WidgetForm(
  {
    onWidgetSave,
    initialWidget,
    showBottomActions = true,
    onSubWidgetSelectionChange,
  },
  ref
) {
  const theme = useSelector((state) => state.themedata.theme);
  const ResponsiveReactGridLayout = useMemo(
    () => WidthProvider(Responsive),
    []
  );

  // Properly initialize state from initialWidget for edit mode
  const [basicProps, setBasicProps] = useState(() => {
    if (initialWidget?.props?.basic) return initialWidget.props.basic;
    // Support backend field widgetname
    const name = initialWidget?.widgetname || initialWidget?.widgetName || "New Widget1";
    return {
      widgetName: name,
      fontSize: 16,
      keyFontSize: 13,
      valueFontSize: 40,
      fontType: "Roboto",
      textColor: 'var(--LK_BrandBlue_1)',
      backgroundColor: "",
      hasCustomBackground: false,
      borderColor: "transparent",
      borderSize: 0,
      borderRadius: 0,
    };
  });
  const [extendedProps, setExtendedProps] = useState(() => {
    if (initialWidget?.props?.extended) {
      const clone = { ...initialWidget.props.extended };
      // Hydrate datapoints from tagprops when datapoints are missing/empty in persisted widgets
      if ((!Array.isArray(clone.datapoints) || clone.datapoints.length === 0) && Array.isArray(clone.tagprops)) {
        clone.datapoints = clone.tagprops.map((tp) => {
          const name = tp.tagname || tp.datapointName || tp.name || tp.title;
          const rawMode = tp.dataType || clone.dataType || "live";
          return {
            datapointName: name,
            tagname: tp.tagname || name,
            key: tp.key,
            id: tp.id || tp.key,
            tagid: tp.tagid || tp.key || tp.id,
            title: tp.title || name,
            name: tp.name || name,
            alias: tp.alias || name,
            storage: Array.isArray(tp.storage) ? tp.storage : [],
            children: Array.isArray(tp.children) ? tp.children : [],
            storageSelection: tp.storageSelection,
            dataType:
              String(rawMode || "live").toLowerCase() === "historic"
                ? "historic"
                : "live",
            textColor: tp.textColor,
            backgroundColor: tp.backgroundColor,
            valuefonttype: tp.valuefonttype,
            valuefontsize: tp.valuefontsize,
            description: tp.description,
          };
        });
      }
      return clone;
    }
    return {};
  });
  const [tabValue, setTabValue] = useState(0);
  const [manageWidgetsView, setManageWidgetsView] = useState(!initialWidget);

  function getMainWidgetType(widget) {
    if (!widget) return "";
    const type = widget.type || widget.widgetType || widget.mainType || "";
    if (subWidgetLists["Charts"].includes(type)) return "Charts";
    if (subWidgetLists["Card"].includes(type)) return "Card";
    if (mainWidgetList.includes(widget.mainType || widget.widgetType))
      return widget.mainType || widget.widgetType;
    return widget.widgetType || "";
  }
  const [selectedWidget, setSelectedWidget] = useState(
    initialWidget ? getMainWidgetType(initialWidget) : ""
  );
  const [selectedSubWidget, setSelectedSubWidget] = useState(
    initialWidget?.type || ""
  );
  const initializedWidgetIdRef = useRef(null);
  const basicPropsRef = useRef(basicProps);

  useEffect(() => {
    basicPropsRef.current = basicProps;
  }, [basicProps]);

  useEffect(() => {
    onSubWidgetSelectionChange?.(Boolean(selectedSubWidget));
  }, [selectedSubWidget, onSubWidgetSelectionChange]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({});

  const getMainWidgetPreview = (widget) => {
    const stroke = "#008CD7";
    const fill = theme === "light" ? "#E8F4FB" : "#2A3A45";

    if (widget === "Card") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="5" y="5" width="34" height="18" rx="3" fill={fill} stroke={stroke} strokeWidth="1.5" />
          <rect x="10" y="10" width="12" height="3" rx="1.5" fill={stroke} />
          <rect x="10" y="16" width="22" height="2.5" rx="1.25" fill={stroke} opacity="0.75" />
        </svg>
      );
    }

    if (widget === "Charts") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="6" y="14" width="5" height="10" rx="1" fill={stroke} />
          <rect x="14" y="10" width="5" height="14" rx="1" fill={stroke} />
          <rect x="22" y="6" width="5" height="18" rx="1" fill={stroke} />
          <polyline points="6,12 14,8 22,10 30,5 38,8" fill="none" stroke={fill} strokeWidth="2" />
        </svg>
      );
    }

    if (widget === "Gauge") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <path d="M7 20 A15 15 0 0 1 37 20" fill="none" stroke={fill} strokeWidth="5" strokeLinecap="round" />
          <path d="M7 20 A15 15 0 0 1 27 8" fill="none" stroke={stroke} strokeWidth="5" strokeLinecap="round" />
          <line x1="22" y1="20" x2="28" y2="12" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }

    if (widget === "Table") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="6" y="5" width="32" height="18" rx="2" fill="none" stroke={stroke} strokeWidth="1.5" />
          <line x1="6" y1="11" x2="38" y2="11" stroke={stroke} strokeWidth="1.2" />
          <line x1="6" y1="17" x2="38" y2="17" stroke={stroke} strokeWidth="1.2" />
          <line x1="18" y1="5" x2="18" y2="23" stroke={stroke} strokeWidth="1.2" />
          <line x1="28" y1="5" x2="28" y2="23" stroke={stroke} strokeWidth="1.2" />
        </svg>
      );
    }

    if (widget === "Control") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="8" y="9" width="28" height="10" rx="5" fill={fill} stroke={stroke} strokeWidth="1.5" />
          <circle cx="16" cy="14" r="4" fill={stroke} />
        </svg>
      );
    }

    if (widget === "Map") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <path d="M12 6 C9 6 7 8.2 7 11 c0 3.8 5 9 5 9 s5-5.2 5-9 c0-2.8-2-5-5-5z" fill={stroke} />
          <circle cx="12" cy="11" r="2" fill={fill} />
          <path d="M22 8 l6-3 8 3 v13 l-6 3 -8-3 -6 3 v-13z" fill="none" stroke={stroke} strokeWidth="1.5" />
        </svg>
      );
    }

    if (widget === "Others") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <circle cx="14" cy="14" r="3" fill={stroke} />
          <circle cx="22" cy="14" r="3" fill={stroke} />
          <circle cx="30" cy="14" r="3" fill={stroke} />
        </svg>
      );
    }

    return null;
  };

  const getSubWidgetPreview = (subWidget) => {
    const stroke = "#008CD7";
    const fill = theme === "light" ? "#E8F4FB" : "#2A3A45";

    if (selectedWidget === "Charts") {
      if (subWidget === "Line Chart" || subWidget === "Area Chart") {
        return (
          <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
            {subWidget === "Area Chart" && (
              <polygon points="2,22 10,16 18,18 26,10 34,12 42,6 42,26 2,26" fill={fill} />
            )}
            <polyline
              points="2,22 10,16 18,18 26,10 34,12 42,6"
              fill="none"
              stroke={stroke}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </svg>
        );
      }

      if (subWidget === "Bar Chart") {
        return (
          <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
            <rect x="4" y="14" width="6" height="12" rx="1" fill={stroke} />
            <rect x="14" y="9" width="6" height="17" rx="1" fill={stroke} />
            <rect x="24" y="5" width="6" height="21" rx="1" fill={stroke} />
            <rect x="34" y="11" width="6" height="15" rx="1" fill={stroke} />
          </svg>
        );
      }

      if (subWidget === "Pie Chart") {
        return (
          <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
            <circle cx="22" cy="14" r="10" fill={fill} stroke={stroke} strokeWidth="2" />
            <path d="M22 14 L22 4 A10 10 0 0 1 31 20 Z" fill={stroke} />
          </svg>
        );
      }

      if (subWidget === "Doughnut Chart") {
        return (
          <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
            <circle cx="22" cy="14" r="10" fill="none" stroke={fill} strokeWidth="8" />
            <path d="M22 4 A10 10 0 1 1 12 14" fill="none" stroke={stroke} strokeWidth="8" strokeLinecap="round" />
          </svg>
        );
      }

      if (subWidget === "Radar Chart") {
        return (
          <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
            <polygon points="22,4 33,10 29,22 15,22 11,10" fill={fill} stroke={stroke} strokeWidth="1.5" />
            <polygon points="22,8 29,12 27,19 17,19 15,12" fill="none" stroke={stroke} strokeWidth="1" opacity="0.8" />
          </svg>
        );
      }

      if (subWidget === "Scatter Chart") {
        return (
          <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
            <circle cx="8" cy="20" r="2.5" fill={stroke} />
            <circle cx="15" cy="13" r="2.5" fill={stroke} />
            <circle cx="23" cy="17" r="2.5" fill={stroke} />
            <circle cx="30" cy="9" r="2.5" fill={stroke} />
            <circle cx="37" cy="14" r="2.5" fill={stroke} />
          </svg>
        );
      }
    }

    if (selectedWidget === "Gauge") {
      if (subWidget === "Linear Gauge") {
        return (
          <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
            <rect x="4" y="10" width="36" height="8" rx="4" fill={fill} />
            <rect x="4" y="10" width="24" height="8" rx="4" fill={stroke} />
          </svg>
        );
      }

      if (subWidget === "Temperature Gauge") {
        return (
          <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
            <rect x="20" y="6" width="4" height="12" rx="2" fill={fill} stroke={stroke} strokeWidth="1.5" />
            <circle cx="22" cy="21" r="5" fill={stroke} />
          </svg>
        );
      }

      if (subWidget === "Circular Gauge" || subWidget === "Semi Circular Gauge" || subWidget === "Speed Gauge") {
        return (
          <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
            <path d="M7 20 A15 15 0 0 1 37 20" fill="none" stroke={fill} strokeWidth="5" strokeLinecap="round" />
            <path d="M7 20 A15 15 0 0 1 27 8" fill="none" stroke={stroke} strokeWidth="5" strokeLinecap="round" />
            <line x1="22" y1="20" x2="28" y2="12" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          </svg>
        );
      }
    }

    // Remaining chart/gauge-like sub widgets from Others group
    if (subWidget === "Sankey Chart") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="4" y="7" width="7" height="5" rx="1" fill={stroke} />
          <rect x="4" y="17" width="7" height="5" rx="1" fill={stroke} />
          <rect x="33" y="10" width="7" height="8" rx="1" fill={stroke} />
          <path d="M11 9 C19 9, 22 12, 33 12" stroke={fill} strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M11 19 C19 19, 22 16, 33 16" stroke={fill} strokeWidth="3" fill="none" strokeLinecap="round" />
        </svg>
      );
    }

    if (subWidget === "Funnel") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <polygon points="6,4 38,4 31,10 13,10" fill={stroke} />
          <polygon points="13,11 31,11 27,17 17,17" fill={fill} />
          <polygon points="17,18 27,18 24,24 20,24" fill={stroke} />
        </svg>
      );
    }

    if (subWidget === "Mixed Chart") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="6" y="14" width="5" height="10" rx="1" fill={fill} />
          <rect x="14" y="9" width="5" height="15" rx="1" fill={fill} />
          <rect x="22" y="11" width="5" height="13" rx="1" fill={fill} />
          <polyline points="6,16 14,7 22,12 30,6 38,9" fill="none" stroke={stroke} strokeWidth="2" />
        </svg>
      );
    }

    if (subWidget === "Treemap") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="5" y="5" width="16" height="18" fill={stroke} />
          <rect x="22" y="5" width="17" height="10" fill={fill} />
          <rect x="22" y="16" width="8" height="7" fill={stroke} />
          <rect x="31" y="16" width="8" height="7" fill={fill} />
        </svg>
      );
    }

    if (subWidget === "Linear Progress Bar") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="4" y="10" width="36" height="8" rx="4" fill={fill} />
          <rect x="4" y="10" width="19" height="8" rx="4" fill={stroke} />
        </svg>
      );
    }

    if (subWidget === "Solar Chart") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <circle cx="22" cy="12" r="6" fill={stroke} />
          <line x1="22" y1="2" x2="22" y2="0" stroke={stroke} strokeWidth="1.5" />
          <line x1="22" y1="24" x2="22" y2="22" stroke={stroke} strokeWidth="1.5" />
          <line x1="12" y1="12" x2="10" y2="12" stroke={stroke} strokeWidth="1.5" />
          <line x1="34" y1="12" x2="32" y2="12" stroke={stroke} strokeWidth="1.5" />
          <line x1="14.5" y1="4.5" x2="13" y2="3" stroke={stroke} strokeWidth="1.5" />
          <line x1="29.5" y1="19.5" x2="31" y2="21" stroke={stroke} strokeWidth="1.5" />
          <line x1="29.5" y1="4.5" x2="31" y2="3" stroke={stroke} strokeWidth="1.5" />
          <line x1="14.5" y1="19.5" x2="13" y2="21" stroke={stroke} strokeWidth="1.5" />
        </svg>
      );
    }

    if (subWidget === "BBT") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <polyline points="5,19 13,14 21,16 29,10 37,12" fill="none" stroke={stroke} strokeWidth="2" />
          <line x1="5" y1="22" x2="39" y2="22" stroke={fill} strokeWidth="1.5" />
        </svg>
      );
    }

    if (subWidget === "PQ Events Pie") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <circle cx="22" cy="14" r="10" fill={fill} stroke={stroke} strokeWidth="2" />
          <path d="M22 14 L22 4 A10 10 0 0 1 30 18 Z" fill={stroke} />
        </svg>
      );
    }

    if (subWidget === "AQI Gauge") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <path d="M7 20 A15 15 0 0 1 37 20" fill="none" stroke={fill} strokeWidth="5" strokeLinecap="round" />
          <path d="M7 20 A15 15 0 0 1 33 11" fill="none" stroke={stroke} strokeWidth="5" strokeLinecap="round" />
          <line x1="22" y1="20" x2="30" y2="11" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }

    if (subWidget === "Switch Gauge") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="8" y="9" width="28" height="10" rx="5" fill={fill} />
          <circle cx="28" cy="14" r="4" fill={stroke} />
        </svg>
      );
    }

    if (subWidget === "Simple Gauge") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <path d="M7 20 A15 15 0 0 1 37 20" fill="none" stroke={fill} strokeWidth="5" strokeLinecap="round" />
          <path d="M7 20 A15 15 0 0 1 24 7" fill="none" stroke={stroke} strokeWidth="5" strokeLinecap="round" />
        </svg>
      );
    }

    if (subWidget === "Device Donut" || subWidget === "Gateway Donut") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <circle cx="22" cy="14" r="9" fill="none" stroke={fill} strokeWidth="6" />
          <path d="M22 5 A9 9 0 1 1 15 20" fill="none" stroke={stroke} strokeWidth="6" strokeLinecap="round" />
        </svg>
      );
    }

    if (subWidget === "DeviceTable" || subWidget === "TableGateway") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="6" y="5" width="32" height="18" rx="2" fill="none" stroke={stroke} strokeWidth="1.5" />
          <line x1="6" y1="11" x2="38" y2="11" stroke={stroke} strokeWidth="1.2" />
          <line x1="6" y1="17" x2="38" y2="17" stroke={stroke} strokeWidth="1.2" />
          <line x1="18" y1="5" x2="18" y2="23" stroke={stroke} strokeWidth="1.2" />
          <line x1="28" y1="5" x2="28" y2="23" stroke={stroke} strokeWidth="1.2" />
        </svg>
      );
    }

    if (subWidget === "Google Map") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <path d="M13 6 C10 6 8 8.2 8 11 c0 3.8 5 9 5 9 s5-5.2 5-9 c0-2.8-2-5-5-5z" fill={stroke} />
          <circle cx="13" cy="11" r="2" fill={fill} />
          <path d="M23 8 l6-3 8 3 v13 l-6 3 -8-3 -6 3 v-13z" fill="none" stroke={stroke} strokeWidth="1.5" />
        </svg>
      );
    }

    if (subWidget === "Status" || subWidget === "Switch") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="8" y="9" width="28" height="10" rx="5" fill={fill} stroke={stroke} strokeWidth="1.5" />
          <circle cx="28" cy="14" r="4" fill={stroke} />
        </svg>
      );
    }

    if (subWidget === "Single Value") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="8" y="5" width="28" height="18" rx="3" fill={fill} stroke={stroke} strokeWidth="1.5" />
          <rect x="15" y="11" width="14" height="6" rx="2" fill={stroke} />
        </svg>
      );
    }

    if (subWidget === "Multi Value") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="7" y="7" width="8" height="14" rx="2" fill={stroke} />
          <rect x="18" y="7" width="8" height="14" rx="2" fill={fill} stroke={stroke} strokeWidth="1.2" />
          <rect x="29" y="7" width="8" height="14" rx="2" fill={stroke} opacity="0.85" />
        </svg>
      );
    }

    if (subWidget === "Value Card") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="6" y="4" width="32" height="20" rx="3" fill={fill} stroke={stroke} strokeWidth="1.5" />
          <text x="22" y="17" textAnchor="middle" fontSize="12" fontWeight="700" fill={stroke}>42</text>
        </svg>
      );
    }

    if (subWidget === "Status Card") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="4" y="3" width="36" height="22" rx="3" fill={fill} stroke={stroke} strokeWidth="1.2" />
          <circle cx="14" cy="11" r="3.5" fill="#22c55e" />
          <circle cx="14" cy="20" r="3.5" fill="#ef4444" />
          <line x1="21" y1="11" x2="36" y2="11" stroke={stroke} strokeWidth="1.2" />
          <line x1="21" y1="20" x2="36" y2="20" stroke={stroke} strokeWidth="1.2" />
        </svg>
      );
    }

    if (subWidget === "Scheduler Status") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="5" y="4" width="34" height="20" rx="3" fill={fill} stroke={stroke} strokeWidth="1.2" />
          <text x="10" y="12" fontSize="5" fill={stroke}>S</text>
          <line x1="14" y1="10" x2="35" y2="10" stroke={stroke} strokeWidth="1" />
          <text x="10" y="19" fontSize="5" fill={stroke}>E</text>
          <line x1="14" y1="17" x2="35" y2="17" stroke={stroke} strokeWidth="1" />
        </svg>
      );
    }

    if (subWidget === "Tag Value Card") {
      return (
        <svg width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
          <rect x="4" y="3" width="36" height="22" rx="3" fill={fill} stroke={stroke} strokeWidth="1.2" />
          <text x="8" y="11" fontSize="4" fill={stroke}>Tag A</text>
          <text x="24" y="11" fontSize="4" fill={stroke}>Tag B</text>
          <line x1="22" y1="4" x2="22" y2="25" stroke={stroke} strokeWidth="0.8" strokeDasharray="2 1" />
          <text x="8" y="19" fontSize="6" fontWeight="bold" fill={stroke}>—</text>
          <text x="24" y="19" fontSize="6" fontWeight="bold" fill={stroke}>—</text>
        </svg>
      );
    }
  };
  // Accept partial updates; always create a new object reference so React re-renders.
  const handleBasicPropsUpdate = useCallback((updatedProps) => {
    setBasicProps((prev) => {
      const keys = Object.keys(updatedProps || {});
      const hasChange = keys.some((k) => prev?.[k] !== updatedProps?.[k]);
      return hasChange ? { ...prev, ...updatedProps } : prev;
    });
    // If there's exactly one datapoint and its name matched the previous widgetName, keep it in sync.
    if (updatedProps.widgetName) {
      setExtendedProps((prev) => {
        if (
          !prev ||
          !Array.isArray(prev.datapoints) ||
          prev.datapoints.length !== 1
        )
          return prev;
        const dp = prev.datapoints[0];
        // Heuristic: if the datapoint name equals old widget name (stored in basicProps before update) or equals previous basicProps.widgetName, sync it.
        if (
          dp.datapointName === basicPropsRef.current?.widgetName ||
          dp.datapointName === prev.widgetName
        ) {
          const newDp = { ...dp, datapointName: updatedProps.widgetName };
          return { ...prev, datapoints: [newDp] };
        }
        return prev;
      });
    }
  }, []);

  const handleExtendedPropsUpdate = useCallback((updatedProps) => {
    setExtendedProps((prev) => {
      const keys = Object.keys(updatedProps || {});
      const hasChange = keys.some((k) => prev?.[k] !== updatedProps?.[k]);
      return hasChange ? { ...prev, ...updatedProps } : prev;
    });
  }, []);

  const handleSaveWidget = () => {
    if (!selectedSubWidget) {
      setDialogConfig({
        dialogTitle: "Error",
        dialogText: "Please select a sub-widget before saving.",
        confirmText: "OK",
        cancelText: null,
        type: "Error",
        handleConfirmDialog: () => setDialogOpen(false),
        handleCloseDialog: () => setDialogOpen(false),
      });
      setDialogOpen(true);
      return;
    }

    // For Charts, ensure chartOptions and chartData are included in extendedProps
    let propsToSave = { ...extendedProps };
    // Logging: raw extended props before harmonization
    
    // Harmonize extended props: if datapoints exist but tagprops not, build tagprops for backend if needed later
    // Always rebuild tagprops from current datapoints to reflect any edits (tag change, colors, storageSelection)
    if (Array.isArray(propsToSave.datapoints)) {
      const previousTagpropsSnapshot = propsToSave.tagprops
        ? JSON.parse(JSON.stringify(propsToSave.tagprops))
        : null;
      propsToSave.tagprops = propsToSave.datapoints.map((dp) => {
        const name = dp.datapointName || dp.tagname || dp.name || dp.title;
        const tagid = dp.tagid || dp.key || dp.id;
        return {
          tagname: name,
          datapointName: name,
          key: tagid,
          id: tagid,
          tagid,
          title: dp.title || name,
          name: dp.name || name,
          alias: dp.alias || name,
          storage: Array.isArray(dp.storage) ? dp.storage : [],
          storageSelection:
            dp.storageSelection ||
            (Array.isArray(dp.storage) && dp.storage[0]) ||
            "AVG",
          children: Array.isArray(dp.children) ? dp.children : [],
          dataType: (dp.dataType || propsToSave.dataType || "live").toLowerCase(),
          textColor: dp.textColor,
          backgroundColor: dp.backgroundColor,
          valuefonttype: dp.valuefonttype,
          valuefontsize: dp.valuefontsize,
          description: dp.description,
        };
      });
      // eslint-disable-next-line no-console
    }
    if ((selectedWidget === "Charts" || selectedWidget === "Gauge" || selectedWidget === "Table") && selectedSubWidget) {
      // If chartOptions is not set, use default
      if (!propsToSave.chartOptions) {
        propsToSave.chartOptions = getDefaultChartOptions(
          selectedSubWidget,
          basicProps,
          extendedProps
        );
        // eslint-disable-next-line no-console
       
      }
      // Optionally, ensure chartData and categories are present
      if (!propsToSave.chartData) {
        propsToSave.chartData = extendedProps?.chartData || [
          120, 200, 150, 80, 70, 110, 130,
        ];
        // eslint-disable-next-line no-console
    
      }
      if (!propsToSave.categories) {
        propsToSave.categories = extendedProps?.categories || [
          "Mon",
          "Tue",
          "Wed",
          "Thu",
          "Fri",
          "Sat",
          "Sun",
        ];
        // eslint-disable-next-line no-console
        
      }
    }
    // Ensure isConfig flag lives at root of props (not inside extended) per required consumer format
    const isConfigFlag = true;
    // Final extended props snapshot
    // eslint-disable-next-line no-console

    // Remove any stray isConfig that may have been present inside extended from legacy saves
    if (propsToSave.isConfig) {
      // eslint-disable-next-line no-console
   
      delete propsToSave.isConfig;
    }
    const newWidget = {
      id: initialWidget?.id || Date.now(),
      widgetname: basicProps.widgetName,
      type: selectedSubWidget,
      props: {
        isConfig: isConfigFlag,
        basic: { ...basicProps },
        extended: propsToSave,
      },
    };

    // Comprehensive widget save log



    if (onWidgetSave) {
      
      onWidgetSave(newWidget);

    }

    // Commenting out the success message display
    /*
    setDialogConfig({
      dialogTitle: "Success",
      dialogText: "Widget saved successfully!",
      confirmText: "OK",
      cancelText: null,
      type: "Success",
      handleConfirmDialog: () => {
        resetValues(); // Reset values after saving
        setDialogOpen(false);
      },
      handleCloseDialog: () => setDialogOpen(false),
    });
    setDialogOpen(true);
    */
  };

  const handleWidgetClick = (widget) => {
    setSelectedWidget(widget);
  };

  const handleSubWidgetClick = (subWidget) => {
    setSelectedSubWidget(subWidget);
    setManageWidgetsView(false); // Switch to widget configuration view
    // If not editing (no initialWidget), reset both basicProps and extendedProps for new subwidget
    if (!initialWidget) {
      setBasicProps({
        widgetName: "New Widget",
        fontSize: 16,
        fontType: "Roboto",
        textColor: "#008cd7",
        backgroundColor: "",
        hasCustomBackground: false,
        borderColor: "transparent",
        borderSize: 0,
        borderRadius: 0,
      });
      setExtendedProps({ ...chartDefaults });
      setTabValue(0);
    }
  };

  const handleBackToManageWidgets = () => {
    resetValues(); 
    setManageWidgetsView(true);
  };

  useImperativeHandle(ref, () => ({
    saveWidget: handleSaveWidget,
    backToManageWidgets: handleBackToManageWidgets,
  }));

  const resetValues = () => {
    setBasicProps({
      widgetName: "New Widget",
      fontSize: 16,
      fontType: "Roboto",
      textColor: "#008cd7",
      backgroundColor: "",
      hasCustomBackground: false,
      borderColor: "transparent",
      borderSize: 0,
      borderRadius: 0,
    });
    setExtendedProps({});
    setSelectedWidget("");
    setSelectedSubWidget("");
    setTabValue(0);
  };

  // Make preview span full width of available columns for the current breakpoint (option D)
  const layoutConfig = {
    i: "1",
    x: 0,
    y: 0,
    w: 12, // full width for lg (auto scaled for smaller breakpoints by RGL)
    h: 6,
    minW: 2,
    minH: 1,
    maxH: 20,
    static: false,
  };

  // Get the latest datapoint info from extendedProps
  const latestDatapoint = extendedProps?.datapoints?.length
    ? extendedProps.datapoints[extendedProps.datapoints.length - 1]
    : null;

  // Initialize edit values only when switching to a different widget (prevents input flicker in deployed/live refresh)
  useEffect(() => {
    const widgetId =
      initialWidget?.id || initialWidget?.widgetid || initialWidget?._id;
    if (!widgetId || initializedWidgetIdRef.current === widgetId) return;

    const incomingName =
      initialWidget?.widgetname || initialWidget?.widgetName || "";
    if (incomingName) {
      setBasicProps((prev) => ({
        ...prev,
        widgetName: incomingName,
      }));
    }
    initializedWidgetIdRef.current = widgetId;
  }, [initialWidget?.id, initialWidget?.widgetid, initialWidget?._id, initialWidget?.widgetname, initialWidget?.widgetName]);
  return (
    <Box
      sx={{
        fontWeight: 400,
        fontSize: 16,
        color: "#7E7E7E",
        borderRadius: 4,
        background: theme === "light" ? "#f8f9fa" : "#1E1E1E",
        // height: "100vh",
      }}
    >
      {manageWidgetsView ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            fontWeight: 400,
            fontSize: 16,
            color: "#7E7E7E",
            borderRadius: 4,
          }}
        >
          {/* <Typography variant="h5" sx={{ mb: 2, fontWeight: 400, fontSize: 20, color: '#7E7E7E', borderRadius: 4 }}>
            Manage Widgets
          </Typography> */}
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  fontSize: 16,
                  color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
                  borderRadius: 4,
                  padding: "10px 5px",
                  padding: "10px 5px",
                  color: "#008CD7",
                  fontWeight: 700,
                  fontSize: "20px",
                }}
              >
                Widgets
              </Typography>
              <div
              style={{
                height:"80vh",
                overflowY: "auto",
              }}
              >
                {mainWidgetList.map((widget) => (
                  <Card
                    key={widget}
                    elevation={2}
                    sx={{
                      backgroundColor: theme === "dark" ? "#1E1E1E" : "#FFFFFF",
                      borderRadius: 2,
                      height: 110,
                      width: 200,
                      m: "10px 5px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontWeight: selectedWidget === widget ? 700 : 400,
                      color: selectedWidget === widget ? "#008CD7" : "#7E7E7E",
                      border:
                        selectedWidget === widget
                          ? "2px solid #008CD7"
                          : "1px solid #ddd",
                      fontSize: 16,
                    }}
                    onClick={() => handleWidgetClick(widget)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ height: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {getMainWidgetPreview(widget)}
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: 16,
                          color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
                          borderRadius: 2,
                          textAlign: "center",
                          px: 1,
                          lineHeight: 1.2,
                        }}
                      >
                        {widget}
                      </Typography>
                    </Box>
                  </Card>
                ))}
              </div>
            </Grid>
            <Grid item xs={9}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  fontSize: 16,
                  color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
                  borderRadius: 4,
                  padding: "10px 5px",
                  color: "#008CD7",
                  fontWeight: 700,
                  fontSize: "20px",
                }}
              >
                Sub Widgets
              </Typography>
              {selectedWidget && subWidgetLists[selectedWidget] ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {subWidgetLists[selectedWidget].map((subWidget) => (
                    <Card
                      key={subWidget}
                      elevation={2}
                      sx={{
                        backgroundColor:
                          theme === "dark" ? "#1E1E1E" : "#FFFFFF",
                        borderRadius: 2,
                        height: 110,
                        width: 150,
                        m: "10px 5px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        border: "1px solid #7E7E7E",
                        fontWeight: 400,
                        fontSize: 16,
                        color: "#7E7E7E",
                      }}
                      onClick={() => handleSubWidgetClick(subWidget)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        {getSubWidgetPreview(subWidget) && (
                          <Box sx={{ height: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {getSubWidgetPreview(subWidget)}
                          </Box>
                        )}
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: 16,
                            color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
                            borderRadius: 2,
                            textAlign: "center",
                            px: 1,
                            lineHeight: 1.2,
                          }}
                        >
                          {subWidget}
                        </Typography>
                      </Box>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: 16,
                    color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
                    borderRadius: 4,
                  }}
                >
                  Select a widget to view sub widgets
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 2,
            width: "100%",
            minHeight: 500,
            fontWeight: 400,
            fontSize: 16,
            color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
            boxShadow: 2,
          }}
        >
          {/* Left: Preview Section */}
          {/* <Box sx={{ width: '45%', display: 'flex', flexDirection: 'column', fontWeight: 400, fontSize: 16, color: theme==='light' ? '#7E7E7E' : '#FFFFFF',
            //  borderRadius: 4, 
            //  background: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)', 
              p: '24px 16px 24px 0',
              minHeight: 500 }}> */}
          <Box
            sx={{
              width: "45%",
              minHeight: 500,
              background: theme === "light" ? "#efefef" : "#2c2c2c",
              // borderRadius: 1,
              p: 1,
            }}
          >
            <ResponsiveReactGridLayout
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
              rowHeight={60}
              margin={[5, 5]}
              allowOverlap={false}
              resizeHandles={["se"]}
              isResizable={true}
              isDraggable={true}
            >
              <div key={1} data-grid={layoutConfig} 
                         style={{ height: "100%" }}>
                <Box sx={{ height: "100%", width: "100%",
                 background: theme === "dark" ? "#2A2F35" : "#F3F5F7"
                 }}>
                  <Card
                    elevation={0}
                    sx={buildWidgetCardSx(
                      { ...basicProps, backgroundColor: resolveWidgetBackground(basicProps, theme) },
                      { height: "100%" }
                    )}
                  >
                    <Box sx={widgetHeaderSx({ ...basicProps, backgroundColor: resolveWidgetBackground(basicProps, theme) })}>
                      <Typography component="h6" sx={widgetTitleSx(basicProps)}>
                        {basicProps.widgetName} 
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                    </Box>
                    <Divider
                      sx={{ borderColor: "var(--CardDivider,#E5E9E8)" }}
                    />
                    <Box sx={widgetContentSx}>
                      {selectedSubWidget === "Value Card" ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: 1,
                            width: "100%",
                            height: "100%",
                            gap: 0.5,
                          }}
                        >
                          {(() => {
                            const keyFontSize = basicProps.keyFontSize || basicProps.fontSize || 13;
                            const showAlias = !!extendedProps?.showAlias;
                            const resolveDP0Name = () => {
                              const dp = extendedProps.datapoints[0];
                              if (showAlias) return dp.alias || dp.datapointName || dp.name || dp.tagname || '—';
                              return dp.datapointName || dp.name || dp.tagname || '—';
                            };
                            return (
                              <>
                          <Typography
                            sx={{
                              fontSize: basicProps.valueFontSize || 40,
                              fontWeight: 400,
                              fontFamily: basicProps.fontType || "Roboto",
                              color: basicProps.textColor,
                              lineHeight: 1.2,
                              textAlign: "center",
                            }}
                          >
                            {extendedProps?.datapoints?.length > 0 ? "0" : "—"}
                          </Typography>
                          {/* {(extendedProps?.dataType === "live" || extendedProps?.dataType === "mixed") && (
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 400,
                                fontFamily: basicProps.fontType || "Roboto",
                                color: theme === "dark" ? "#C7CED3" : "#56636E",
                                textAlign: "center",
                              }}
                            >
                              {new Date().toLocaleString()}
                            </Typography>
                          )} */}
                          {/* Tag name + unit at the bottom */}
                          {extendedProps?.datapoints?.length > 0 && (
                            <Typography
                              sx={{
                                fontSize: keyFontSize,
                                fontWeight: 400,
                                fontFamily: basicProps.fontType || "Roboto",
                                color: theme === "dark" ? "#C7CED3" : "#56636E",
                                textAlign: "center",
                                pt: 0.5,
                              }}
                            >
                              {resolveDP0Name()} 
                              {(extendedProps.datapoints[0].storageSelection || extendedProps.datapoints[0].unit) ? (
                                <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 400 }}>
                                  ({extendedProps.datapoints[0].storageSelection || extendedProps.datapoints[0].unit})
                                </span>
                              ) : null}
                            </Typography>
                          )}
                              </>
                            );
                          })()}
                        </Box>
                      ) : selectedSubWidget === "Status Card" ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            flex: 1,
                            p: 1.5,
                            gap: 1,
                            overflow: "hidden",
                            padding: 0,
                          }}
                        >
                          {extendedProps?.datapoints?.length > 0 ? (
                            <>
                              <TableContainer
                                component={Paper}
                                elevation={0}
                                sx={{
                                  border: `1px solid ${theme === "dark" ? "#3B4248" : "#E5E9E8"}`,
                                   background: theme === "dark" ? "#2A2F35" : "#F3F5F7",
                                  borderRadius: 1,
                                  boxShadow: "none",
                                  flex: 1,
                                  overflow: "auto",
                                }}
                              >
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow 
                                    sx={{ background: theme === "dark" ? "#2A2F35" : "#F3F5F7" }}>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: theme === "dark" ? "#C7CED3" : "#5A646C",
                                          fontFamily: basicProps.fontType || "Roboto",
                                          textTransform: "uppercase",
                                          letterSpacing: 0.5,
                                          borderBottom: `1px solid ${theme === "dark" ? "#3B4248" : "#E5E9E8"}`,
                                          py: 0.5,
                                          px: 1,
                                          background: theme === "dark" ? "#2A2F35" : "#F3F5F7",
                                        }}
                                      >
                                        Device
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: theme === "dark" ? "#C7CED3" : "#5A646C",
                                          fontFamily: basicProps.fontType || "Roboto",
                                          textTransform: "uppercase",
                                          letterSpacing: 0.5,
                                          borderBottom: `1px solid ${theme === "dark" ? "#3B4248" : "#E5E9E8"}`,
                                          py: 0.5,
                                          px: 1,
                                          background: theme === "dark" ? "#2A2F35" : "#F3F5F7",
                                        }}
                                      >
                                        Tag
                                      </TableCell>
                                      {/* <TableCell
                                        sx={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: theme === "dark" ? "#C7CED3" : "#5A646C",
                                          fontFamily: basicProps.fontType || "Roboto",
                                          textTransform: "uppercase",
                                          letterSpacing: 0.5,
                                          borderBottom: `1px solid ${theme === "dark" ? "#3B4248" : "#E5E9E8"}`,
                                          py: 0.5,
                                          px: 1,
                                          background: theme === "dark" ? "#2A2F35" : "#F3F5F7",
                                        }}
                                      >
                                        Last Sync Time
                                      </TableCell> */}
                                      <TableCell
                                        align="center"
                                        sx={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: theme === "dark" ? "#C7CED3" : "#5A646C",
                                          fontFamily: basicProps.fontType || "Roboto",
                                          textTransform: "uppercase",
                                          letterSpacing: 0.5,
                                          borderBottom: `1px solid ${theme === "dark" ? "#3B4248" : "#E5E9E8"}`,
                                          py: 0.5,
                                          px: 1,
                                          background: theme === "dark" ? "#2A2F35" : "#F3F5F7",
                                        }}
                                      >
                                        Status
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {extendedProps.datapoints.map((dp, idx) => {
                                      const deviceName = dp.parentDevice?.title || dp.parentDevice?.name || "—";
                                      const tagName = dp.datapointName || dp.name || dp.tagname || "—";
                                      const tagUnit = dp.unit || dp.storageSelection;
                                      const ringColor = dp.activeColor || "#22c55e";
                                      const previewSyncTime = (dp?.dataType || extendedProps?.dataType || "live") === "historic"
                                        ? (extendedProps?.dateRange?.end
                                            ? new Date(extendedProps.dateRange.end).toLocaleString()
                                            : "—")
                                        : new Date().toLocaleString();
                                      return (
                                        <TableRow
                                          key={dp.tagid || dp.key || idx}
                                          sx={{
                                            background: theme === "dark" ? "#2C2C2C" : "#FFFFFF",
                                            // "&:hover": { background: theme === "dark" ? "#343A40" : "#FAFBFC" },
                                            // "&:not(:last-child) td": { borderBottom: `1px solid ${theme === "dark" ? "#3B4248" : "#E5E9E8"}` },
                                          }}
                                        >
                                          <TableCell
                                            sx={{
                                              fontSize: 11,
                                              fontWeight: 600,
                                              color: theme === "dark" ? "#FFFFFF" : "#1F2A33",
                                              fontFamily: basicProps.fontType || "Roboto",
                                              py: 0.75,
                                              px: 1,
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              maxWidth: 80,
                                              borderBottom: "none",
                                            }}
                                            title={deviceName}
                                          >
                                            {deviceName}
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              fontSize: 11,
                                              fontWeight: 500,
                                              color: theme === "dark" ? "#D5DADD" : "#374151",
                                              fontFamily: basicProps.fontType || "Roboto",
                                              py: 0.75,
                                              px: 1,
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              maxWidth: 100,
                                              borderBottom: "none",
                                            }}
                                            title={tagUnit ? `${tagName} (${tagUnit})` : tagName}
                                          >
                                            {tagName.split(".")[1]}
                                            {/* {tagUnit ? (
                                              <Typography
                                                component="span"
                                                sx={{
                                                  fontSize: 10,
                                                  fontWeight: 500,
                                                  color: theme === "dark" ? "#AEB7BD" : "#6B7280",
                                                  fontFamily: basicProps.fontType || "Roboto",
                                                  ml: 0.5,
                                                }}
                                              >
                                                ({tagUnit})
                                              </Typography>
                                            ) : null} */}
                                          </TableCell>
                                          {/* <TableCell
                                            sx={{
                                              fontSize: 11,
                                              fontWeight: 500,
                                              color: theme === "dark" ? "#D5DADD" : "#374151",
                                              fontFamily: basicProps.fontType || "Roboto",
                                              py: 0.75,
                                              px: 1,
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              maxWidth: 140,
                                              borderBottom: "none",
                                            }}
                                            title={previewSyncTime}
                                          >
                                            {previewSyncTime}
                                          </TableCell> */}
                                          <TableCell
                                            align="center"
                                            sx={{
                                              py: 0.75,
                                              px: 1,
                                              borderBottom: "none",
                                            }}
                                          >
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                              <Box
                                                sx={{
                                                  width: 20,
                                                  height: 20,
                                                  borderRadius: "50%",
                                                  background: ringColor,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.6)",
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: "50%",
                                                    background: "#FFFFFF",
                                                  }}
                                                />
                                              </Box>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              {/* {(extendedProps?.dataType === "live" || extendedProps?.dataType === "mixed") && (
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 400,
                                    fontFamily: basicProps.fontType || "Roboto",
                                    color: theme === "dark" ? "#AEB7BD" : "#6B7280",
                                    textAlign: "right",
                                  }}
                                >
                                  Last updated: {new Date().toLocaleString()}
                                </Typography>
                              )} */}
                            </>
                          ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 1 }}>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Box sx={{ width: 20, height: 20, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.6)" }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "#fff" }} />
                                </Box>
                                <Box sx={{ width: 20, height: 20, borderRadius: "50%", background: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.6)" }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "#fff" }} />
                                </Box>
                              </Box>
                              <Typography sx={{ fontSize: 11, color: theme === "dark" ? "#C7CED3" : "#6B7280", textAlign: "center" }}>
                                Add tags to see status
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ) : selectedSubWidget === "Tag Value Card" ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            flex: 1,
                            p: 1.5,
                            gap: 1,
                          }}
                        >
                          {(() => {
                            const dp0 = extendedProps?.datapoints?.[0] || null;
                            const dp1 = extendedProps?.datapoints?.[1] || null;
                            const unit0 = dp0?.unit || dp0?.storageSelection;
                            const unit1 = dp1?.unit || dp1?.storageSelection;
                            const showAlias = !!extendedProps?.showAlias;
                            const name0 = dp0 ? (showAlias ? (dp0.alias || dp0.datapointName || dp0.name || "Tag A") : (dp0.datapointName || dp0.name || "Tag A")) : "Tag A";
                            const name1 = dp1 ? (showAlias ? (dp1.alias || dp1.datapointName || dp1.name || null) : (dp1.datapointName || dp1.name || null)) : null;
                            const hasTwoItems = !!dp1;
                            const keyFontSize = basicProps.keyFontSize || basicProps.fontSize || 13;
                            const valFontSize = basicProps.valueFontSize ? Math.max(16, Math.round((basicProps.valueFontSize || 40) * 0.45)) : 18;
                            const secColor = theme === "dark" ? "#C7CED3" : "#56636E";
                            return (
                              <>
                                <Box sx={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 1 }}>
                                  <Box sx={{ display: "grid", gridTemplateColumns: hasTwoItems ? "1fr 1fr" : "1fr", columnGap: 2, alignItems: "end", width: "min(100%, 460px)", mx: "auto" }}>
                                    <Typography sx={{ fontSize: keyFontSize, fontWeight: 400, fontFamily: basicProps.fontType || "Roboto", color: secColor, textAlign: "center" }}>
                                      {name0}{unit0 ? <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 400 }}>({unit0})</span> : null}
                                    </Typography>
                                    {hasTwoItems && (
                                      <Typography sx={{ fontSize: keyFontSize, fontWeight: 400, fontFamily: basicProps.fontType || "Roboto", color: secColor, textAlign: "center" }}>
                                        {name1}{unit1 ? <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 400 }}>({unit1})</span> : null}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box sx={{ display: "grid", gridTemplateColumns: hasTwoItems ? "1fr 1fr" : "1fr", columnGap: 2, alignItems: "baseline", width: "min(100%, 460px)", mx: "auto" }}>
                                    <Typography sx={{ fontSize: valFontSize, fontWeight: 400, fontFamily: basicProps.fontType || "Roboto", color: basicProps.textColor, textAlign: "center" }}>—</Typography>
                                    {hasTwoItems && (
                                      <Typography sx={{ fontSize: valFontSize, fontWeight: 400, fontFamily: basicProps.fontType || "Roboto", color: basicProps.textColor, textAlign: "center" }}>—</Typography>
                                    )}
                                  </Box>
                                </Box>
                                {/* {(extendedProps?.dataType === "live" || extendedProps?.dataType === "mixed") && (
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 400,
                                      fontFamily: basicProps.fontType || "Roboto",
                                      color: theme === "dark" ? "#AEB7BD" : "#6B7280",
                                      textAlign: "center",
                                      width: "100%"
                                    }}
                                  >
                                    {new Date().toLocaleString()}
                                  </Typography>
                                )} */}
                              </>
                            );
                          })()}
                        </Box>
                      ) : selectedSubWidget === "Scheduler Status" ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            flex: 1,
                            justifyContent: "center",
                            alignItems: "center",
                            position: "relative",
                            gap: 1,
                            p: 1.5,
                          }}
                        >
                          {(() => {
                            const startDp = extendedProps?.datapoints?.[0] || null;
                            const stopDp = extendedProps?.datapoints?.[1] || null;
                            const startUnit = startDp?.unit || startDp?.storageSelection;
                            const stopUnit = stopDp?.unit || stopDp?.storageSelection;
                            const keyFontSize = basicProps.keyFontSize || basicProps.fontSize || 13;
                            return (
                              <>
                                <Box sx={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 1 }}>
                                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, alignItems: "end", width: "min(100%, 460px)", mx: "auto" }}>
                                    <Typography sx={{ fontSize: keyFontSize, fontWeight: 400, fontFamily: basicProps.fontType || "Roboto", color: theme === "dark" ? "#C7CED3" : "#56636E", textAlign: "center" }}>
                                      Start time
                                    </Typography>
                                    <Typography sx={{ fontSize: keyFontSize, fontWeight: 400, fontFamily: basicProps.fontType || "Roboto", color: theme === "dark" ? "#C7CED3" : "#56636E", textAlign: "center" }}>
                                      Stop time
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, alignItems: "baseline", width: "min(100%, 460px)", mx: "auto" }}>
                                    <Typography sx={{ fontSize: basicProps.valueFontSize ? Math.max(16, Math.round((basicProps.valueFontSize || 40) * 0.45)) : 18, fontWeight: 400, fontFamily: basicProps.fontType || "Roboto", color: basicProps.textColor, textAlign: "center" }}>
                                      —
                                      {startUnit ? <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 400, color: theme === "dark" ? "#AEB7BD" : "#6B7280" }}>({startUnit})</span> : null}
                                    </Typography>
                                    <Typography sx={{ fontSize: basicProps.valueFontSize ? Math.max(16, Math.round((basicProps.valueFontSize || 40) * 0.45)) : 18, fontWeight: 400, fontFamily: basicProps.fontType || "Roboto", color: basicProps.textColor, textAlign: "center" }}>
                                      —
                                      {stopUnit ? <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 400, color: theme === "dark" ? "#AEB7BD" : "#6B7280" }}>({stopUnit})</span> : null}
                                    </Typography>
                                  </Box>
                                </Box>
                                {/* {(extendedProps?.dataType === "live" || extendedProps?.dataType === "mixed") && (
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 400,
                                      fontFamily: basicProps.fontType || "Roboto",
                                      color: theme === "dark" ? "#AEB7BD" : "#6B7280",
                                      textAlign: "center",
                                      position: "absolute",
                                      right: 8,
                                      bottom: 6,
                                    }}
                                  >
                                    {new Date().toLocaleString()}
                                  </Typography>
                                )} */}
                              </>
                            );
                          })()}
                        </Box>
                      ) : selectedSubWidget && (isChartType(selectedSubWidget) || isGaugeType(selectedSubWidget)) ? (
                        <EChart
                          key={`${selectedSubWidget}-${JSON.stringify(extendedProps)}`}
                          chartType={selectedSubWidget}
                          options={getDefaultChartOptions(
                            selectedSubWidget,
                            basicProps,
                            extendedProps,
                            // Create preview data based on widget type
                            (() => {
                              const showAlias = !!extendedProps?.showAlias;
                              const resolvePreviewName = (dp, idx) => {
                                if (showAlias) return dp.alias || dp.datapointName || dp.name || `Item ${idx + 1}`;
                                return dp.datapointName || dp.name || `Item ${idx + 1}`;
                              };
                              // For pie/doughnut charts, create data from datapoints
                              if ((selectedSubWidget === 'Pie Chart' || selectedSubWidget === 'Doughnut Chart' || selectedSubWidget === 'PQ Events Pie' || selectedSubWidget === 'Device Donut' || selectedSubWidget === 'Gateway Donut') && extendedProps?.datapoints) {
                                return extendedProps.datapoints.map((dp, idx) => ({
                                  value: 100 + idx * 50, // Dummy values for preview
                                  name: resolvePreviewName(dp, idx),
                                  datapointName: resolvePreviewName(dp, idx),
                                  color: dp.color || dp.backgroundColor // Use the color from datapoint
                                }));
                              }
                              // For Device Donut without datapoints, return demo data
                              if (selectedSubWidget === 'Device Donut') {
                                return [
                                  { value: 25, name: 'Connected', y: 25 },
                                  { value: 8, name: 'Disconnected', y: 8 }
                                ];
                              }
                              // For Gateway Donut without datapoints, return demo data
                              if (selectedSubWidget === 'Gateway Donut') {
                                return [
                                  { value: 12, name: 'Connected', y: 12 },
                                  { value: 3, name: 'Disconnected', y: 3 }
                                ];
                              }
                              // For DeviceTable, return empty as it uses table-specific rendering
                              if (selectedSubWidget === 'DeviceTable') {
                                return [];
                              }
                              // For TableGateway, return empty as it uses table-specific rendering
                              if (selectedSubWidget === 'TableGateway') {
                                return [];
                              }
                              // For gauges including Others gauge types, use value from first datapoint or default
                              if (isGaugeType(selectedSubWidget) || selectedSubWidget === 'AQI Gauge' || selectedSubWidget === 'Switch Gauge' || selectedSubWidget === 'Simple Gauge') {
                                const dpValue = extendedProps?.datapoints?.[0]?.value ?? 
                                               extendedProps?.datapoints?.[0]?.latestValue ?? 
                                               75;
                                const secondaryValue = extendedProps?.datapoints?.[1]?.value ?? 
                                                      extendedProps?.datapoints?.[1]?.latestValue ?? 
                                                      60;
                                return [{ value: dpValue, y: dpValue }, { value: secondaryValue, y: secondaryValue }];
                              }
                              // For Line/Area/Bar charts with configured datapoints, build named series for legend
                              if ((selectedSubWidget === 'Line Chart' || selectedSubWidget === 'Area Chart' || selectedSubWidget === 'Bar Chart') && Array.isArray(extendedProps?.datapoints) && extendedProps.datapoints.length > 0) {
                                const now = Date.now();
                                return extendedProps.datapoints.map((dp, idx) => ({
                                  tagid: dp.tagid || dp.key || dp.id || String(idx),
                                  name: resolvePreviewName(dp, idx),
                                  color: dp.lineStyle?.color || dp.color || dp.textColor,
                                  points: [
                                    { x: now - 300000, y: 50 + idx * 15 },
                                    { x: now - 240000, y: 60 + idx * 10 },
                                    { x: now - 180000, y: 45 + idx * 20 },
                                    { x: now - 120000, y: 70 + idx * 5 },
                                    { x: now - 60000,  y: 55 + idx * 12 },
                                    { x: now,          y: 65 + idx * 8 },
                                  ],
                                }));
                              }
                              // For Sankey Chart, return empty to use demo data from builder
                              if (selectedSubWidget === 'Sankey Chart') {
                                return [];
                              }
                              // For Funnel, return empty to use demo data from builder
                              if (selectedSubWidget === 'Funnel') {
                                return [];
                              }
                              // For other Others charts, return undefined to use demo data
                              return undefined;
                            })()
                          )}
                          style={{
                            height: "100%",
                            width: "100%",
                            minWidth: 250,
                            borderRadius: 4,
                          }}
                        />
                      ) : selectedSubWidget === 'DeviceTable' ? (
                        <Box sx={{ width: '100%', overflow: 'auto', backgroundColor: 'var(--secondary-backgroundLK)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #E0E5E4', backgroundColor: 'var(--secondary-backgroundLK)', color: 'var(--grey-variation-tableLK)' }}>Device Name</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #E0E5E4', backgroundColor: 'var(--secondary-backgroundLK)', color: 'var(--grey-variation-tableLK)' }}>Serial Number</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #E0E5E4', backgroundColor: 'var(--secondary-backgroundLK)', color: 'var(--grey-variation-tableLK)' }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[
                                { id: 1, name: 'Device-01', serialNumber: 'SN-12345', status: 'Connected' },
                                { id: 2, name: 'Device-02', serialNumber: 'SN-12346', status: 'Connected' },
                                { id: 3, name: 'Device-03', serialNumber: 'SN-12347', status: 'Disconnected' },
                                { id: 4, name: 'Device-04', serialNumber: 'SN-12348', status: 'Connected' }
                              ].map((row) => (
                                <tr key={row.id} style={{ borderBottom: '1px solid #F0F2F4' }}>
                                  <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 500, color: 'var(--grey-variation-tablerowsLK)' }}>{row.name}</td>
                                  <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 500, color: 'var(--grey-variation-tablerowsLK)' }}>{row.serialNumber}</td>
                                  <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 500, color: row.status === 'Connected' ? '#00943D' : '#DC272D' }}>{row.status}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      ) : selectedSubWidget === 'TableGateway' ? (
                        <Box sx={{ width: '100%', overflow: 'auto', backgroundColor: 'var(--secondary-backgroundLK)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #E0E5E4', backgroundColor: 'var(--secondary-backgroundLK)', color: 'var(--grey-variation-tableLK)' }}>Gateway Name</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #E0E5E4', backgroundColor: 'var(--secondary-backgroundLK)', color: 'var(--grey-variation-tableLK)' }}>Serial Number</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #E0E5E4', backgroundColor: 'var(--secondary-backgroundLK)', color: 'var(--grey-variation-tableLK)' }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[
                                { id: 1, name: 'Gateway-01', serialNumber: 'GW-12345', status: 'Connected' },
                                { id: 2, name: 'Gateway-02', serialNumber: 'GW-12346', status: 'Connected' },
                                { id: 3, name: 'Gateway-03', serialNumber: 'GW-12347', status: 'Disconnected' },
                                { id: 4, name: 'Gateway-04', serialNumber: 'GW-12348', status: 'Connected' }
                              ].map((row) => (
                                <tr key={row.id} style={{ borderBottom: '1px solid #F0F2F4' }}>
                                  <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 500, color: 'var(--grey-variation-tablerowsLK)' }}>{row.name}</td>
                                  <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 500, color: 'var(--grey-variation-tablerowsLK)' }}>{row.serialNumber}</td>
                                  <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 500, color: row.status === 'Connected' ? '#00943D' : '#DC272D' }}>{row.status}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      ) : selectedSubWidget && isTableType(selectedSubWidget) ? (
                        <Box sx={{ width: '100%', overflow: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, fontSize: 12, borderBottom: '1px solid #E0E5E4' }}>Tag Name</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, fontSize: 12, borderBottom: '1px solid #E0E5E4' }}>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(extendedProps?.datapoints && extendedProps.datapoints.length ? extendedProps.datapoints : [{ datapointName: 'No Data', value: '—', key: 'none' }]).map((dp, idx) => (
                                <tr key={dp.key || dp.id || dp.datapointName || idx} style={{ backgroundColor: dp.backgroundColor || basicProps.backgroundColor, color: dp.textColor || basicProps.textColor }}>
                                  <td style={{ padding: '6px 8px', fontSize: 12, fontWeight: 600, borderBottom: '1px solid #F0F2F4' }}>
                                    {dp.datapointName || dp.name || '—  {dp.datapointName |'}  
                                  </td>
                                  <td style={{ padding: '6px 8px', fontSize: 12, fontWeight: 500, textAlign: 'right', borderBottom: '1px solid #F0F2F4' }}>
                                    {(() => {
                                      const raw = dp.value ?? dp.latestValue;
                                      if (raw === null || raw === undefined || Number.isNaN(raw)) return '—';
                                      if (typeof raw === 'number') {
                                        const abs = Math.abs(raw);
                                        let str = abs >= 1000 ? raw.toLocaleString(undefined, { maximumFractionDigits: 2 }) : raw.toFixed(2);
                                        str = str.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
                                        return str;
                                      }
                                      return String(raw);
                                    })()}
                                    {dp.unit ? <span style={{ marginLeft: 4, color: '#56636E', fontSize: 10 }}>{dp.unit}</span> : null}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                            overflow: "auto",
                          }}
                        >
                          {extendedProps?.datapoints &&
                          extendedProps.datapoints.length > 0 ? (
                            extendedProps.datapoints.map((dp, idx) => (
                              <Box
                                key={
                                  dp.key ||
                                  `${dp.datapointName || "Datapoint"}-${idx}`
                                }
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  background:
                                    dp.backgroundColor ||
                                    basicProps.backgroundColor,
                                  color: dp.textColor || basicProps.textColor,
                                  fontFamily:
                                    dp.valuefonttype || basicProps.fontType,
                                  fontSize: dp.valuefontsize
                                    ? dp.valuefontsize
                                    : basicProps.fontSize || 16,
                                  borderBottom: "1px solid #E4E9EC",
                                  // borderRadius: 1.5,
                                  p: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    width: "75%",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontSize: 12,
                                    fontWeight: 400,
                                  }}
                                >
                                  {(extendedProps?.showAlias ? (dp.alias || dp.datapointName) : dp.datapointName) || "Datapoint"}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    fontWeight: 400,
                                  }}
                                >
                                  0
                                </Typography>
                              </Box>
                            ))
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                background: basicProps.backgroundColor,
                                color: basicProps.textColor,
                                p: 1,
                                borderBottom: "1px solid #E4E9EC",
                                // borderRadius: 1.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  width: "75%",
                                  fontSize: 12,
                                  fontWeight: 400,
                                }}
                              >
                                No Datapoints
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 400,
                                }}
                              >
                                0
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Box>
              </div>
            </ResponsiveReactGridLayout>
          </Box>
          {/* </Box> */}
          {/* Right: Tabs for Props */}
          <Box
            sx={{
              width: "55%",
              display: "flex",
              flexDirection: "column",
              fontWeight: 400,
              fontSize: 16,
              color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
              borderRadius: 0,
              background: theme === "light" ? "#f8f9fa" : "#1E1E1E",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              // p: "24px 0 24px 16px",
              minHeight: 500,
            }}
          >
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
              <Tab
                label="Basic Props"
                sx={{ color: theme === "light" ? "#7E7E7E" : "#FFFFFF" }}
              />
              <Tab
                label="Extended Props"
                sx={{ color: theme === "light" ? "#7E7E7E" : "#FFFFFF" }}
              />
            </Tabs>
            <Box sx={{ mt: 2 }}>
              {tabValue === 0 && (
                <BasicProps
                  theme={theme}
                  initialProps={basicProps}
                  syncKey={`${initialWidget?.id || initialWidget?.widgetid || initialWidget?._id || "new"}:${selectedSubWidget || ""}`}
                  onUpdate={handleBasicPropsUpdate}
                  widgetType={selectedSubWidget}
                />
              )}
              {tabValue === 1 && (
                <Extendedprops
                  widgettype={selectedWidget}
                  subWidgetType={selectedSubWidget}
                  extendedProps={extendedProps}
                  callbackpropsupdate={handleExtendedPropsUpdate}
                />
              )}
            </Box>
            {/* {showBottomActions && (
              <Box sx={{ mt: 3 }}>
                <CustomButton
                  buttonText={"Save Widget"}
                  onClick={handleSaveWidget}
                />
                {!initialWidget && (
                  <CustomButton
                    buttonText={"Back to Manage Widgets"}
                    onClick={handleBackToManageWidgets}
                    sx={{ ml: 2 }}
                  />
                )}
              </Box>
            )} */}
          </Box>
        </Box>
      )}
      {/* Custom Dialog Box */}
      <CustomDialogBox
        dialogTitle={dialogConfig.dialogTitle}
        dialogText={dialogConfig.dialogText}
        openDialog={dialogOpen}
        handleCloseDialog={dialogConfig.handleCloseDialog}
        handleConfirmDialog={dialogConfig.handleConfirmDialog}
        confirmText={dialogConfig.confirmText}
        cancelText={dialogConfig.cancelText}
        type={dialogConfig.type}
      />
    </Box>
  );
});

export default WidgetForm;